import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as IconNoDataDictionary } from "@/assets/svgs/no-data-dictionary.svg";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";
import Search from '@/componentsDS/Search/index';
import useDataSchemaVersioning from "@/hooks/use-data-schema-versioning/index";
import { prepareFetchParams } from "@/hooks/use-data-schema-versioning/index";
import usePage from "@/hooks/use-page";
import { useNavigate } from "@/lib/router-dom";
import PropTypes from 'prop-types';
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDsvAdmin } from "@/hooks/use-dsv-admin/index";

// ============ Helper Functions ============

function getPageStart(pageSize, pageNr) {
	return pageSize * pageNr;
}

function getPageLabel(total, pageSize, pageNr) {
	const start = Math.max(getPageStart(pageSize, pageNr), 0);
	const end = Math.min(getPageStart(pageSize, pageNr + 1), total);
	return `${start + 1} - ${end}`;
}

function formatDate(dateStr) {
	if (!dateStr) return '-';
	const date = new Date(dateStr);
	return date.toISOString().split('T')[0];
}

// ============ Status Badge ============

function StatusBadge({ status }) {
	const statusMap = {
		'ACTIVE': { className: 'dsv-status--active', label: 'Active' },
		'DRAFT': { className: 'dsv-status--draft', label: 'Draft' },
		'DEPRECATED': { className: 'dsv-status--migrating', label: 'Deprecated' },
		'RETIRED': { className: 'dsv-status--retired', label: 'Retired' },
	};
	const config = statusMap[status] || statusMap['DRAFT'];
	return <span className={`dsv-status ${config.className}`}>{config.label}</span>;
}

StatusBadge.propTypes = {
	status: PropTypes.string
};

// ============ Table Header ============

function DSVTableHeader() {
	return (
		<div className="data-table__table__head">
			<div className="data-table__table__head__row">
				<div className="data-table__table__head__title dsv-table__col--name">
					Data Element
				</div>
				<div className="data-table__table__head__title dsv-table__col--classification">
					Classification
				</div>
				<div className="data-table__table__head__title dsv-table__col--description">
					Description / Definition
				</div>
				<div className="data-table__table__head__title dsv-table__col--fields">
					Data Fields
				</div>
				<div className="data-table__table__head__title dsv-table__col--version">
					Latest Version
				</div>
				<div className="data-table__table__head__title dsv-table__col--date">
					Published Date
				</div>
				<div className="data-table__table__head__title dsv-table__col--status">
					Status
				</div>
				<div className="data-table__table__head__title dsv-table__col--actions">
					Actions
				</div>
			</div>
		</div>
	);
}

// ============ Table List ============

function DSVTableList({ data, onViewDetails }) {
	if (data?.length > 0) {
		return (
			<>
				{data.map((item) => {
					// Show active version as primary when a newer draft/deprecated version exists
					const displayItem = item.activeVersion || item;
					const hasPendingVersion = !!item.activeVersion;

					const schema = displayItem.elementSchemaJson || displayItem.elementSchema;
					const fieldCount = schema?.properties ? Object.keys(schema.properties).length : '-';

					return (
						<div className="data-table__table__body__rows" key={item.id}>
							<div className="data-table__table__body__contents data-table__table__body__content--heavy dsv-table__col--name">
								{item.elementName}
							</div>
							<div className="data-table__table__body__contents dsv-table__col--classification">
								{item.type === "DOCUMENT" ? "Document" : item.type === "EVENT" ? "Event" : item.type}
							</div>
							<div className="data-table__table__body__contents dsv-table__col--description">
								{schema?.description || '-'}
							</div>
							<div className="data-table__table__body__contents dsv-table__col--fields">
								{fieldCount}
							</div>
							<div className="data-table__table__body__contents dsv-table__col--version">
								<span className={`dsv-version-badge dsv-version-badge--${displayItem.status?.toLowerCase() || 'draft'}`}>
									{displayItem.version || '-'}
								</span>
								{hasPendingVersion && (
									<span className="dsv-draft-indicator">
										{item.status} v{item.version}
									</span>
								)}
							</div>
							<div className="data-table__table__body__contents dsv-table__col--date">
								{formatDate(displayItem.publishedDate)}
							</div>
							<div className="data-table__table__body__contents dsv-table__col--status">
								<StatusBadge status={displayItem.status} />
							</div>
							<div className="data-table__table__body__contents dsv-table__col--actions">
								<span
									className="data-table__table__body__view-details"
									role="button"
									tabIndex={0}
									onClick={() => onViewDetails(item)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											onViewDetails(item);
										}
									}}
								>
									View Details
								</span>
							</div>
						</div>
					);
				})}
			</>
		);
	}

	return (
		<div className="data-table__empty">
			<div className="data-table__empty__icon">
				<IconNoDataDictionary />
				<span>Data Element not found in selected configuration.</span>
			</div>
		</div>
	);
}

DSVTableList.propTypes = {
	data: PropTypes.array,
	onViewDetails: PropTypes.func,
};

// ============ Main Page Component ============

const DSVListPage = () => {
	const { goTo } = useNavigate();
	const canEditSchema = useDsvAdmin();
	const [currentPage, setCurrentPage] = useState(1);
	const [pageLimit, setPageLimit] = useState(10);
	const pageSizeOptions = [10, 20, 30, 40, 50];

	const pageTitle = "Data Element";
	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{ label: '', link: '/' },
			{ label: pageTitle, link: '' },
		],
	});

	const [searchKey, setSearchKey] = useState("");
	const [statusFilter, setStatusFilter] = useState("ALL");
	const [typeFilter, setTypeFilter] = useState("ALL");

	const {
		fetchListWithActiveVersions,
		items,
		allData,
		loading,
	} = useDataSchemaVersioning();

	// ============ Fetch Data ============

	function fetchData() {
		const params = prepareFetchParams({
			status: statusFilter,
			type: typeFilter,
			// When searching, fetch all items for client-side filtering
			...(searchKey
				? { all: true }
				: { currentPage, pageSize: pageLimit }
			),
		});
		fetchListWithActiveVersions(params);
	}

	useEffect(() => {
		return () => setCurrentPage(1);
	}, [statusFilter, typeFilter, searchKey, pageLimit]);

	useEffect(() => {
		const delayDebounceFn = setTimeout(fetchData, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [currentPage, statusFilter, typeFilter, searchKey, pageLimit]);

	// ============ Pagination ============

	function goToPrev() {
		if (currentPage > 1) setCurrentPage((prev) => prev - 1);
	}

	function goToNext() {
		setCurrentPage((prev) => prev + 1);
	}

	// ============ Navigation Handlers ============

	function handleViewDetails(item) {
		goTo(`/dsv/${item.dataElementId}`);
	}

	function clearFilter() {
		setStatusFilter("ALL");
		setTypeFilter("ALL");
		setSearchKey("");
	}

	function isFilterDefault() {
		return currentPage === 1 && statusFilter === "ALL" && typeFilter === "ALL" && !searchKey;
	}

	// ============ Search handlers ============
	const handleSearchChange = (value) => {
		setSearchKey(value);
	};
	const handleSearchCancel = () => setSearchKey("");

	// Client-side search filter (BE doesn't have searchKey for this endpoint)
	// When searching, all items are fetched and we paginate client-side
	const searchFiltered = searchKey
		? items.filter(item =>
			(item.elementName || '').toLowerCase().includes(searchKey.toLowerCase())
		)
		: items;

	const isClientPaginated = !!searchKey;
	const clientPageCount = isClientPaginated ? Math.ceil(searchFiltered.length / pageLimit) || 1 : null;
	const filteredItems = isClientPaginated
		? searchFiltered.slice((currentPage - 1) * pageLimit, currentPage * pageLimit)
		: searchFiltered;

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			<div className="dsv-title-header">
				<div className="dsv-title-header__left">
					<div className="dsv-title-header__title">{pageTitle}</div>
				</div>
				{canEditSchema && (
					<div className="dsv-title-header__right">
						<button
							className="dsv-btn dsv-btn--primary"
							onClick={() => goTo('/dsv/new/update-schema?mode=create')}
							data-testid="create-de-btn"
						>
							Create Data Element
						</button>
					</div>
				)}
			</div>

			<hr />

			{/* Filters */}
			<div className="overview">
				<div className="overview_filter">
					<div className="overview_filter__cols">
						<div className="overview_filter__col input-field">
							<div className="overview_filter__col__top overview_filter__col__title">
								Classification
							</div>
							<div className="overview_filter__col__top overview_filter__col__bottom--background overview_filter__col__width">
								<select
									className="dsv-modal__select"
									value={typeFilter}
									onChange={(e) => setTypeFilter(e.target.value)}
								>
									<option value="ALL">All (Default)</option>
									<option value="DOCUMENT">Document</option>
									<option value="EVENT">Event</option>
									<option value="ETR">ETR</option>
								</select>
							</div>
						</div>

						<div className="overview_filter__col input-field">
							<div className="overview_filter__col__top overview_filter__col__title">
								Status
							</div>
							<div className="overview_filter__col__top overview_filter__col__bottom--background overview_filter__col__width">
								<select
									className="dsv-modal__select"
									value={statusFilter}
									onChange={(e) => setStatusFilter(e.target.value)}
								>
									<option value="ALL">All</option>
									<option value="DRAFT">Draft</option>
									<option value="ACTIVE">Active</option>
									<option value="DEPRECATED">Deprecated</option>
									<option value="RETIRED">Retired</option>
								</select>
							</div>
						</div>

						<div className="overview_filter__col">
							<div className="overview_filter__col__top">&nbsp;</div>
							<div className="overview_filter__col__bottom overview_filter__col__top-background">
								<button
									type="button"
									className="overview_filter__no-border"
									onClick={() => clearFilter()}
									disabled={isFilterDefault()}
								>
									Clear filters
								</button>
							</div>
						</div>
					</div>
				</div>

				{/* Table Content */}
				<div className="overview__content">
					<div className="data-table__content">
						<div className="data-table__content__cols">
							<div className="data-table__content__col">
								<div className="data-table__table">
									{/* Search */}
									<div className="data-table__table__head__row">
										<div className="data-table__table__head__row__left data-table__table__left__width data-table__table__display">
											<Search
												id="dsv-search"
												value={searchKey || ''}
												placeholder="Search by Data Element"
												onChange={handleSearchChange}
												onCancel={handleSearchCancel}
											/>
										</div>
									</div>

									<DSVTableHeader />

									<DSVTableList
										data={filteredItems}
										onViewDetails={handleViewDetails}
									/>

									{/* Pagination */}
									{filteredItems?.length > 0 && (allData || isClientPaginated) && (
										<div className="data-table__table__footer">
											<div className="data-table__table__footer__row">
												<div className="data-table__table__footer__row__left">
													<div className="data-table__table__pagination">
														<input
															className="data-table__table__pagination__page"
															type="number"
															value={currentPage}
															onChange={(e) =>
																setCurrentPage(
																	e.target.value === "" ? 1 : Number.parseInt(e.target.value, 10)
																)
															}
															min="1"
														/>
														<span className="data-table__table__pagination__page__meta">
															{" "}of {isClientPaginated ? clientPageCount : (allData?.pageCount || 1)} pages{" "}
														</span>
														<button
															className="data-table__table__pagination__prev"
															onClick={() => goToPrev()}
															disabled={currentPage === 1}
														>
															<IconChevronDown />
														</button>
														<button
															className="data-table__table__pagination__next"
															onClick={() => goToNext()}
															disabled={currentPage === (isClientPaginated ? clientPageCount : allData?.pageCount)}
														>
															<IconChevronDown />
														</button>
													</div>
												</div>
												<div className="data-table__table__footer__row__right">
													<div className="data-table__table__meta dsv-table__meta">
														Showing{" "}
														{getPageLabel(isClientPaginated ? searchFiltered.length : allData?.count, pageLimit, currentPage - 1)}{" "}
														of {isClientPaginated ? searchFiltered.length : (allData?.count || 0)} entries,{" "}
														<select
															className="dsv-table__pagination__page-size"
															value={pageLimit}
															onChange={(e) => setPageLimit(Number.parseInt(e.target.value, 10))}
														>
															{pageSizeOptions.map((size) => (
																<option key={size} value={size}>{size}</option>
															))}
														</select>
														{" "}entries per page
													</div>
												</div>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Loading overlay */}
			{loading &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src={assetUrl("/img/loading.gif")} alt="loading" />
						</div>
					</div>,
					document.body
				)}
		</>
	);
};

export default DSVListPage;
